import { KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from 'react';

function clipLeft(el: HTMLElement | null): number {
  for (let p = el?.parentElement; p; p = p.parentElement) {
    if (getComputedStyle(p).overflowX !== 'visible') return p.getBoundingClientRect().left;
  }
  return 0;
}

/** The "⋯" menu for a row's rarer actions: opens on click, Enter or Space; closes on Escape and outside click. */
export default function MoreMenu({
  label,
  items,
}: {
  label: string;
  items: { label: string; onClick: () => void; destructive?: boolean; disabled?: boolean }[];
}) {
  const [open, setOpen] = useState(false);
  const [alignLeft, setAlignLeft] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const rect = button.current?.getBoundingClientRect();
    const width = menu.current?.offsetWidth ?? 200;
    setAlignLeft(Boolean(rect && rect.right - width < clipLeft(button.current) + 8));
    const first = box.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])');
    first?.focus();
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const move = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const list = Array.from(
      box.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [],
    );
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
    list[next]?.focus();
  };

  if (!items.length) return null;
  return (
    <div ref={box} className="relative inline-block text-left">
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] rounded-sm border border-line-strong bg-transparent text-ink hover:bg-selected"
      >
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="3" cy="8" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="13" cy="8" r="1.5" />
        </svg>
      </button>
      {open ? (
        <div
          ref={menu}
          role="menu"
          aria-label={label}
          onKeyDown={move}
          className={`absolute ${alignLeft ? 'left-0' : 'right-0'} z-40 mt-1 min-w-[200px] rounded-md bg-surface shadow-lift border border-line py-1`}
        >
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
              className={`block w-full text-left px-4 min-h-[44px] text-[15px] leading-[22px] hover:bg-selected disabled:text-ink-3 disabled:hover:bg-transparent ${
                it.destructive ? 'text-warning' : 'text-ink'
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
